/**
 * TaskResponseForm — `TaskResponse` submission for multi-respondent
 * (`audienceType` set) Tasks (issue #1680, epic #1674).
 *
 * Same generic key/value editor rationale as `TaskOutputForm` — no
 * `taskType` has registered an output schema yet (`TaskTypeRegistry`,
 * `backend/src/utils/taskTypeRegistry.ts` ships empty), so this renders any
 * `TaskResponse.output` shape as editable string fields rather than guessing
 * a tailored one.
 *
 * The caller responds as themselves (`respondentType: 'User'`) or as one of
 * their own teams (`respondentType: 'Team'`) — the backend rejects
 * responding on behalf of anyone else (`backend/src/routes/tasks.ts`'s
 * `POST /:id/responses`). One `TaskResponse` per respondent per Task — a
 * repeat submission replaces the prior one, it does not append a second row.
 */

import { useState } from 'react';
import { ForgeButton, ForgeIcon } from '@tylertech/forge-react';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/text-field';
import { submitTaskResponse, ApiError, type TaskResponseRecord } from '../../lib/apiClient';
import './TaskResponseForm.css';

const ForgeTextField = 'forge-text-field' as React.ElementType;

export interface TaskResponseFormProps {
  taskId: string;
  /** Team ids the current user belongs to — offered as a respond-as-team option. */
  userTeams: readonly string[];
  currentUserId: string;
  onSubmitted: (response: TaskResponseRecord) => void;
}

interface OutputRow {
  key: string;
  value: string;
}

export function TaskResponseForm({
  taskId,
  userTeams,
  currentUserId,
  onSubmitted,
}: TaskResponseFormProps): JSX.Element {
  const [respondentType, setRespondentType] = useState<'User' | 'Team'>('User');
  const [respondentTeamId, setRespondentTeamId] = useState<string>(userTeams[0] ?? '');
  const [rows, setRows] = useState<OutputRow[]>([{ key: '', value: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateRow = (index: number, field: 'key' | 'value', value: string): void => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addRow = (): void => setRows((prev) => [...prev, { key: '', value: '' }]);
  const removeRow = (index: number): void => setRows((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      const output: Record<string, unknown> = {};
      for (const row of rows) {
        const key = row.key.trim();
        if (key) {
          // key comes from the respondent's own free-text field-name input,
          // never used to access an existing object's prototype/internals —
          // a plain write into a fresh, caller-owned object literal.
          // eslint-disable-next-line security/detect-object-injection
          output[key] = row.value;
        }
      }
      const response = await submitTaskResponse(taskId, {
        respondentType,
        respondentId: respondentType === 'User' ? currentUserId : respondentTeamId,
        output,
      });
      onSubmitted(response);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="task-response-form" data-testid="task-response-form">
      <p className="forge-typography--body2 task-response-form__hint">
        Submit your response to this task. Submitting again replaces your prior response.
      </p>

      {userTeams.length > 0 && (
        <div className="task-response-form__respondent-picker">
          <ForgeButton
            type="button"
            variant={respondentType === 'User' ? 'raised' : 'outlined'}
            onClick={() => setRespondentType('User')}
            aria-pressed={respondentType === 'User'}
          >
            Respond as myself
          </ForgeButton>
          <ForgeButton
            type="button"
            variant={respondentType === 'Team' ? 'raised' : 'outlined'}
            onClick={() => setRespondentType('Team')}
            aria-pressed={respondentType === 'Team'}
          >
            Respond as my team
          </ForgeButton>
          {respondentType === 'Team' && (
            <select
              aria-label="Respond as team"
              value={respondentTeamId}
              onChange={(e) => setRespondentTeamId(e.target.value)}
            >
              {userTeams.map((teamId) => (
                <option key={teamId} value={teamId}>
                  {teamId}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {rows.map((row, index) => (
        <div className="task-response-form__row" key={index}>
          <ForgeTextField className="task-response-form__field">
            <input
              type="text"
              placeholder="Field name"
              aria-label={`Response field name ${index + 1}`}
              value={row.key}
              onChange={(e) => updateRow(index, 'key', e.target.value)}
            />
          </ForgeTextField>
          <ForgeTextField className="task-response-form__field">
            <input
              type="text"
              placeholder="Value"
              aria-label={`Response field value ${index + 1}`}
              value={row.value}
              onChange={(e) => updateRow(index, 'value', e.target.value)}
            />
          </ForgeTextField>
          <ForgeButton
            type="button"
            variant="text"
            onClick={() => removeRow(index)}
            aria-label={`Remove response field ${index + 1}`}
          >
            <ForgeIcon name="remove" aria-hidden="true" />
          </ForgeButton>
        </div>
      ))}

      <ForgeButton type="button" variant="text" onClick={addRow}>
        <ForgeIcon name="add_circle" slot="start" aria-hidden="true" />
        Add field
      </ForgeButton>

      {error && (
        <p className="forge-typography--body2 task-response-form__error" role="alert">
          {error}
        </p>
      )}

      <div className="task-response-form__actions">
        <ForgeButton
          type="button"
          variant="raised"
          onClick={() => void handleSubmit()}
          disabled={submitting}
        >
          <ForgeIcon name="send" slot="start" aria-hidden="true" />
          {submitting ? 'Submitting…' : 'Submit response'}
        </ForgeButton>
      </div>
    </div>
  );
}
